import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip, message, Radio, Popover } from 'antd';
import { CompassOutlined } from '@ant-design/icons';
import { ArrowRightToLine } from 'lucide-react';
import dayjs from 'dayjs';
import { useTaskDrawer } from '../TaskDrawerContext';
import { formatHHMMSS } from '../utils/timeFormatters';
import MilestoneCompletionModal from './MilestoneCompletionModal';
import { updateTaskStatus } from '../../api/projectManagementApi';
import { useProjectTour } from '../../tour/ProjectManagementTourContext';

export default function TaskHeader() {
    const { startTour } = useProjectTour();
    const {
        task,
        drawerStack,
        popTo,
        pushTask,
        displayActualTimeMs,
        editTitle,
        setEditTitle,
        isOverrun,
        isCompleted,
        statusUpdating,
        attendance,
        activeAttendanceTask,
        isTimerRunning,
        shareUrl,
        sharingLoading,
        handleCompleteTask,
        handleShare,
        handleRevokeShare,
        copyInternalLink,
        handleStartTimer,
        handleStopTimer,
        handleStatusChange,
        closeAll,
        onClose,
        handleSafeClose,
        mode,
        itemType,
        setItemType,
        onTaskUpdated,
        currentAdminId,
        currentAdmin,
    } = useTaskDrawer();

    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [completingLoading, setCompletingLoading] = useState(false);

    const textareaRef = useRef(null);

    const adjustTextareaHeight = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, []);

    useEffect(() => {
        adjustTextareaHeight();
    }, [editTitle, task?._id, adjustTextareaHeight]);

    useEffect(() => {
        const handleResize = () => adjustTextareaHeight();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [adjustTextareaHeight]);

    const isMilestone = task?.itemType === 'milestone' || itemType === 'milestone';

    const onCompleteButtonClick = () => {
        if (isMilestone) {
            setCompletionModalOpen(true);
        } else {
            handleCompleteTask();
        }
    };

    const handleConfirmMilestoneCompletion = async (comments, verifiedChecklist) => {
        setCompletingLoading(true);
        try {
            const extra = verifiedChecklist ? { deliverableChecklist: verifiedChecklist } : {};
            const { data } = await updateTaskStatus(task._id, 'Completed', '', comments, extra);
            message.success('Milestone completed and synced to CRE successfully!');
            setCompletionModalOpen(false);
            if (onTaskUpdated) onTaskUpdated(data?.result);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to complete milestone.');
        } finally {
            setCompletingLoading(false);
        }
    };

    const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
    );

    const internalLink = task ? `${window.location.origin}/project-management?task=${task.taskId || task._id}` : '';

    const sharePopoverContent = (
        <div className="w-80 p-2 space-y-4">
            {/* Internal Team Link */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">
                    <span className="material-symbols-outlined text-sm text-[#03488a] dark:text-[#a7c8ff]">lock</span>
                    <span>Internal Team Link</span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Opens the task drawer for signed-in team members.
                </p>
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                    <input
                        type="text"
                        readOnly
                        value={internalLink}
                        onClick={(e) => e.target.select()}
                        className="bg-transparent text-xs text-slate-700 dark:text-slate-300 px-1 w-full focus:outline-none truncate font-mono"
                    />
                    <button
                        onClick={copyInternalLink}
                        className="px-2.5 py-1 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900 text-[#03488a] dark:text-[#a7c8ff] rounded text-xs font-bold transition-colors whitespace-nowrap cursor-pointer"
                    >
                        Copy
                    </button>
                </div>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-800" />

            {/* Public Guest Link */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">
                        <span className="material-symbols-outlined text-sm text-emerald-600 dark:text-emerald-400">public</span>
                        <span>Public Guest Link</span>
                    </div>
                    {shareUrl && (
                        <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-800 font-semibold">
                            Active (7-day)
                        </span>
                    )}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Sanitized view for external clients. No login required.
                </p>

                {shareUrl ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                            <input
                                type="text"
                                readOnly
                                value={shareUrl}
                                onClick={(e) => e.target.select()}
                                className="bg-transparent text-xs text-slate-700 dark:text-slate-300 px-1 w-full focus:outline-none truncate font-mono"
                            />
                            <button
                                onClick={() => {
                                    navigator.clipboard?.writeText?.(shareUrl);
                                    message.success('Public guest link copied!');
                                }}
                                className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded text-xs font-bold transition-colors whitespace-nowrap cursor-pointer"
                            >
                                Copy
                            </button>
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={handleRevokeShare}
                                disabled={sharingLoading}
                                className="text-[11px] text-rose-600 hover:text-rose-700 dark:text-rose-400 font-semibold flex items-center gap-1 hover:underline cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-xs">link_off</span>
                                Revoke guest link
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={handleShare}
                        disabled={sharingLoading}
                        className="w-full py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-sm">add_link</span>
                        {sharingLoading ? 'Generating...' : 'Generate Guest Link'}
                    </button>
                )}
            </div>
        </div>
    );

    const parentOrMilestone = (() => {
        if (task?.parentId) {
            const isObj = typeof task.parentId === 'object';
            const id = isObj ? (task.parentId._id || task.parentId.id) : task.parentId;
            const title = isObj ? (task.parentId.title || task.parentId.taskId || task.parentId.name) : 'Parent Task';
            const isMs = isObj ? (task.parentId.itemType === 'milestone') : false;
            return { id, title: title || 'Parent Item', isMilestone: isMs };
        }
        if (task?.milestoneId) {
            const isObj = typeof task.milestoneId === 'object';
            const id = isObj ? (task.milestoneId._id || task.milestoneId.id) : task.milestoneId;
            const title = isObj ? (task.milestoneId.title || task.milestoneId.name) : null;
            return { id, title: title || 'Milestone', isMilestone: true };
        }
        return null;
    })();

    return (
        <header className="border-b border-[#e7e8e9] dark:border-[#303030] bg-white dark:bg-[#1f1f1f] px-5 py-3">
            <div className="flex justify-between items-start mb-3 gap-4">
                <div className="space-y-1 flex-1 min-w-0">
                    {drawerStack && drawerStack.length > 1 ? (
                        <div className="flex items-center flex-wrap gap-1.5 text-xs text-ed-on-surface-variant font-bold mb-2">
                            {drawerStack.map((item, idx) => {
                                const isLast = idx === drawerStack.length - 1;
                                const isItemMilestone = item.itemType === 'milestone';
                                return (
                                    <React.Fragment key={item.id || idx}>
                                        <button
                                            onClick={() => !isLast && popTo(idx)}
                                            disabled={isLast}
                                            className={`flex items-center gap-1 transition-colors px-2 py-0.5 rounded-md ${isLast
                                                ? 'text-[#03488a] dark:text-[#a7c8ff] bg-blue-50 dark:bg-blue-950/60 cursor-default'
                                                : 'hover:text-[#03488a] hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-slate-600 dark:text-slate-400'
                                                }`}
                                        >
                                            <span className={isItemMilestone ? 'text-amber-500 font-bold' : 'text-slate-400 font-bold'}>
                                                {isItemMilestone ? '◆' : '○'}
                                            </span>
                                            <span className="truncate max-w-[220px]" title={item.title || ''}>{item.title || `Item #${String(item.id).slice(-6)}`}</span>
                                        </button>
                                        {!isLast && <span className="material-symbols-outlined text-sm text-slate-400">chevron_right</span>}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    ) : parentOrMilestone ? (
                        <div className="flex items-center flex-wrap gap-1.5 text-xs text-ed-on-surface-variant font-bold mb-2">
                            <button
                                onClick={() => {
                                    if (parentOrMilestone.id && pushTask) {
                                        pushTask(parentOrMilestone.id, 'view', parentOrMilestone.title, null);
                                    }
                                }}
                                className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-[#03488a] dark:hover:text-[#a7c8ff] hover:bg-slate-100 dark:hover:bg-slate-800 px-2 py-0.5 rounded-md transition-colors cursor-pointer"
                                title={parentOrMilestone.isMilestone ? "Open Milestone" : "Open Parent Task"}
                            >
                                <span className={parentOrMilestone.isMilestone ? 'text-amber-500 font-bold' : 'text-slate-400 font-bold'}>
                                    {parentOrMilestone.isMilestone ? '◆' : '○'}
                                </span>
                                <span className="truncate max-w-[220px]" title={parentOrMilestone.title}>{parentOrMilestone.title}</span>
                            </button>
                            <span className="material-symbols-outlined text-sm text-slate-400">chevron_right</span>
                            <span className="flex items-center gap-1 text-[#03488a] dark:text-[#a7c8ff] bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded-md cursor-default">
                                <span className={isMilestone ? 'text-amber-500 font-bold' : 'text-slate-400 font-bold'}>
                                    {isMilestone ? '◆' : '○'}
                                </span>
                                <span className="truncate max-w-[260px]" title={task?.title || ''}>{task?.title || `Item #${String(task?._id).slice(-6)}`}</span>
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-ed-on-surface-variant font-label text-sm">
                            <span className="font-bold text-primary dark:text-slate-300 text-base">#{task?.taskNum || task?.taskId || task?._id?.slice(-6)}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1">
                            {mode === 'create' && (
                                <div className="flex items-center gap-1 mb-1">
                                    <span className="text-xs font-semibold text-[#65676b] dark:text-slate-400">
                                        {isMilestone ? "Milestone Title" : "Task Title"}
                                    </span>
                                    <span className="text-red-500 font-bold">*</span>
                                    {!editTitle.trim() && (
                                        <span className="text-xs text-red-500 dark:text-red-400">Required</span>
                                    )}
                                </div>
                            )}
                            <textarea
                                ref={textareaRef}
                                data-tour="task-title-input"
                                rows={1}
                                value={editTitle}
                                onChange={(event) => {
                                    const val = event.target.value.replace(/[\r\n]+/g, ' ');
                                    setEditTitle(val);
                                    event.target.style.height = 'auto';
                                    event.target.style.height = `${event.target.scrollHeight}px`;
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        event.currentTarget.blur();
                                    }
                                }}
                                placeholder={isMilestone ? "Milestone Title" : "Task Title"}
                                autoFocus={mode === 'create'}
                                className={`w-full text-2xl font-headline font-extrabold text-[#191c1d] dark:text-white tracking-tight bg-transparent border-b transition-colors resize-none overflow-hidden block break-words leading-tight hover:border-[#e7e8e9] dark:hover:border-[#303030] focus:border-primary dark:focus:border-slate-500 focus:outline-none py-0.5 ${mode === 'create' && !editTitle.trim()
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-transparent'
                                    }`}
                            />
                        </div>
                    </div>
                </div>

                {mode !== 'create' && !isMilestone && (
                    <div data-tour="task-timer-widget" className="flex items-center gap-4 bg-[#f8f9fa] dark:bg-[#141414] px-5 py-3 rounded-xl border border-[#e7e8e9] dark:border-[#303030] shrink-0 ml-auto min-w-[200px] justify-end">
                        {(() => {
                            // Only the task's assignee and super_admin can start/stop tracking
                            const assigneeId = task?.assignee?._id || task?.assignee;
                            const isAssignee = assigneeId && String(assigneeId) === String(currentAdminId);
                            const isSuperAdmin = currentAdmin?.role === 'super_admin';
                            const canTrack = isAssignee || isSuperAdmin;
                            const trackingTooltip = canTrack ? '' : 'Only the assignee can track this task';

                            return isTimerRunning ? (
                                <Tooltip title={trackingTooltip}>
                                    <span>
                                        <button
                                            onClick={() => canTrack && handleStopTimer()}
                                            disabled={!canTrack}
                                            className={`w-10 h-10 flex items-center justify-center transition-colors shadow-sm shrink-0 ${!canTrack ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 opacity-50 cursor-not-allowed' : 'bg-[#ffdad6] text-[#93000a] hover:bg-[#ba1a1a] hover:text-white'}`}
                                            style={{ borderRadius: '50%' }}
                                            title={canTrack ? 'Stop Tracking' : trackingTooltip}
                                        >
                                            <span className="material-symbols-outlined text-xl">stop</span>
                                        </button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title={!canTrack ? trackingTooltip : (isCompleted ? 'Task is completed' : '')}>
                                    <span>
                                        <button
                                            onClick={() => canTrack && handleStartTimer()}
                                            disabled={isCompleted || !canTrack}
                                            className={`w-10 h-10 flex items-center justify-center transition-colors shadow-sm shrink-0 ${(isCompleted || !canTrack) ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 opacity-50 cursor-not-allowed' : 'bg-[#c4eed0] text-[#0f5223] hover:bg-[#146c2e] hover:text-white'}`}
                                            style={{ borderRadius: '50%' }}
                                            title={canTrack ? 'Start Tracking' : trackingTooltip}
                                        >
                                            <span className="material-symbols-outlined text-xl">play_arrow</span>
                                        </button>
                                    </span>
                                </Tooltip>
                            );
                        })()}
                        <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Logged Today</span>
                            <div className="text-2xl font-headline font-bold tracking-tighter text-[#191c1d] dark:text-white flex items-center">
                                <span className={isOverrun ? 'text-error animate-pulse' : ''}>{formatHHMMSS(displayActualTimeMs)}</span>
                                <span className="text-slate-400 font-medium text-sm ml-1.5">/ {task?.estimatedTime || '04:30:00'}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {mode === 'create' ? (
                <div className="flex items-center justify-between gap-4">
                    <div data-tour="task-item-type-switch" className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Work Item Type:</span>
                        <Radio.Group
                            value={itemType}
                            onChange={(e) => setItemType(e.target.value)}
                            buttonStyle="solid"
                            size="small"
                        >
                            <Radio.Button value="task">○ Task</Radio.Button>
                            <Radio.Button value="milestone">◆ Milestone</Radio.Button>
                        </Radio.Group>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-tour="create-drawer-tour-btn"
                            onClick={() => startTour('createTask')}
                            className="bg-white dark:bg-[#262626] hover:bg-slate-50 dark:hover:bg-[#323232] text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-[#3a3a3a] px-3 py-1.5 rounded-xl font-label font-bold text-xs flex items-center gap-2 transition-all cursor-pointer shadow-2xs active:scale-95"
                            title="Guide for creating tasks and milestones"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00AAF1]"></span>
                            </span>
                            <CompassOutlined className="text-sm text-[#03488a] dark:text-[#a7c8ff]" />
                            <span>Tour Guide</span>
                        </button>

                        <Tooltip title="Close drawer" placement="left">
                            <button
                                type="button"
                                onClick={() => handleSafeClose ? handleSafeClose(closeAll || onClose) : (closeAll || onClose)?.()}
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-[#262626] dark:hover:bg-[#323232] text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-[#3a3a3a] transition-colors cursor-pointer shadow-2xs"
                                title="Close drawer"
                            >
                                <ArrowRightToLine size={16} />
                            </button>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div data-tour="task-header-status" className="flex gap-2 items-center">
                        {isMilestone && (
                            <span className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                                <span className="text-amber-500 font-bold">◆</span> MILESTONE
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Dynamic Complete / Incomplete Toggle Button */}
                        {isCompleted ? (
                            <button
                                onClick={() => handleStatusChange('In Progress')}
                                disabled={statusUpdating}
                                className="px-3.5 py-1.5 rounded-lg font-label font-bold text-sm flex items-center shadow-sm transition-all active:scale-95 bg-rose-600 hover:bg-rose-700 text-white border border-rose-600 hover:opacity-95 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Revert back to In Progress"
                            >
                                <span>Mark as Incomplete</span>
                            </button>
                        ) : (
                            <button
                                onClick={onCompleteButtonClick}
                                disabled={statusUpdating}
                                className="px-3.5 py-1.5 rounded-lg font-label font-bold text-sm flex items-center gap-1.5 shadow-sm transition-all active:scale-95 bg-gradient-primary dark:bg-[#262626] dark:border dark:border-[#303030] text-white dark:hover:bg-[#303030] hover:opacity-90 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-sm font-bold">check_circle</span>
                                <span>{isMilestone ? 'Complete Milestone' : 'Complete Task'}</span>
                            </button>
                        )}

                        {/* Share Task — available in all environments */}
                        <div data-tour="task-share-btn">
                            <Popover
                                content={sharePopoverContent}
                                title={<span className="font-bold text-xs text-slate-800 dark:text-slate-100">Share Task</span>}
                                trigger="click"
                                placement="bottomRight"
                            >
                                <button
                                    className="bg-slate-100 hover:bg-slate-200 dark:bg-[#262626] dark:hover:bg-[#323232] text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-[#3a3a3a] px-3 py-1.5 rounded-lg font-label font-bold text-sm flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
                                    title="Share Task"
                                >
                                    <span className="material-symbols-outlined text-sm font-bold text-[#03488a] dark:text-[#a7c8ff]">share</span>
                                    <span>Share</span>
                                    {shareUrl && (
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" title="Public link active" />
                                    )}
                                </button>
                            </Popover>
                        </div>

                        <button
                            type="button"
                            onClick={() => startTour('taskDrawer')}
                            className="bg-slate-100 hover:bg-slate-200 dark:bg-[#262626] dark:hover:bg-[#323232] text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-[#3a3a3a] px-2.5 py-1.5 rounded-lg font-label font-bold text-xs flex items-center gap-1 transition-colors cursor-pointer shadow-2xs"
                            title="Take Task Details Tour"
                        >
                            <CompassOutlined className="text-[#03488a] dark:text-[#a7c8ff]" />
                            <span className="hidden sm:inline">Tour</span>
                        </button>

                        {/* Collapse / Close drawer button — inside the header */}
                        <Tooltip title="Close drawer" placement="left">
                            <button
                                type="button"
                                onClick={() => handleSafeClose ? handleSafeClose(closeAll || onClose) : (closeAll || onClose)?.()}
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-[#262626] dark:hover:bg-[#323232] text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-[#3a3a3a] transition-colors cursor-pointer shadow-2xs"
                                title="Close drawer"
                            >
                                <ArrowRightToLine size={16} />
                            </button>
                        </Tooltip>
                    </div>
                </div>
            )}

            {task?.rejectionNote && isMilestone && !isCompleted && (
                <div className="mt-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-lg p-2.5 text-xs flex items-start gap-2">
                    <span className="text-rose-600 font-bold text-sm">⚠️</span>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-rose-700 dark:text-rose-300">
                                Client Rejected Milestone {task.revision ? `(Revision ${task.revision})` : ''}:
                            </span>
                            <span className="text-[10px] text-rose-500 font-medium">
                                {task.rejectedBy ? `by ${task.rejectedBy}` : ''} {task.rejectedAt ? `on ${dayjs(task.rejectedAt).format('MMM D, YYYY h:mm A')}` : ''}
                            </span>
                        </div>
                        <p className="text-rose-900 dark:text-rose-200 mt-0.5 font-medium">{task.rejectionNote}</p>
                    </div>
                </div>
            )}

            <MilestoneCompletionModal
                open={completionModalOpen}
                onClose={() => setCompletionModalOpen(false)}
                onConfirm={handleConfirmMilestoneCompletion}
                loading={completingLoading}
                milestone={task || {}}
            />
        </header>
    );
}
